/**
 * Intention — shown before every session, before the emotion picker.
 *
 * t('intention.whatBroughtYou')
 * Free text, 1-3 sentences. Anchors the session and makes the
 * emotion picker contextual ("Now that you've said that out loud...").
 *
 * The intention text feeds into Claude's system prompt for Phase 1,
 * making the opening mirror dramatically more personal.
 */
import { useState } from 'react'
import { useScreenId } from '../../lib/useScreenId'
import { useTranslation } from 'react-i18next'
import VoiceButton from '../ui/VoiceButton'

interface IntentionProps {
  firstName: string
  returnContext?: { pattern: string; cardTitle?: string } | null
  onComplete: (intentionText: string) => void
}

export default function Intention({ firstName, returnContext, onComplete }: IntentionProps) {
  useScreenId('intention')
  const { t } = useTranslation()
  const [text, setText] = useState('')

  function handleContinue() {
    onComplete(text.trim())
  }

  return (
    <div className="flex-1 flex flex-col page-enter">
      <div className="flex-1 flex flex-col px-6 py-10 max-w-xl mx-auto w-full">

        <div className="mb-10">
          <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#9B72C0' }}>
            {t('intention.beforeWeBegin')}
          </p>
        </div>

        {/* Returning user context — shown softly above the question */}
        {returnContext?.pattern && (
          <div
            className="mb-6 px-4 py-3 rounded-xl message-appear"
            style={{
              // Violet-tinted background — Intention shared accent at ~4% opacity
              background: 'rgba(155,114,192,0.04)',
              // Left-border accent recipe for small inline context cards
              borderTop: '1px solid #E4D5EE',
              borderRight: '1px solid #E4D5EE',
              borderBottom: '1px solid #E4D5EE',
              borderLeft: '3px solid #9B72C0',
            }}
          >
            <p className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: '#9B72C0' }}>
              {t('intention.welcomeBack')}
            </p>
            <p className="text-sm leading-relaxed font-display italic" style={{ color: '#4B3F62' }}>
              {returnContext.cardTitle
                ? t('intention.lastTimeCard', { title: returnContext.cardTitle })
                : t('intention.lastTimePattern', { pattern: returnContext.pattern })
              }
            </p>
            <p className="text-xs mt-1" style={{ color: '#9B8FAA' }}>
              {t('intention.freeToGoDeeper')}
            </p>
          </div>
        )}

        <div className="mb-8 message-appear">
          <h2 className="font-display text-2xl leading-snug mb-3"
            style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
            {firstName ? t('intention.whatBroughtYouName', { name: firstName }) : t('intention.whatBroughtYou')}
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>
            {t('intention.sentenceOrTwo')}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <VoiceButton
            onTranscript={t => setText(prev => prev ? `${prev} ${t}` : t)}
            accentColor="#9B72C0"
          >
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={t('intention.placeholderFull')}
              rows={5}
              maxLength={2000}
              autoFocus
              className="w-full px-4 py-4 rounded-xl border text-sm leading-relaxed resize-none focus:outline-none"
              style={{ borderColor: '#C4B5D4', background: 'white', color: '#1C1128', paddingRight: '2.75rem' }}
              onFocus={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = '0 0 0 3px rgba(155,114,192,0.12)' }}
              onBlur={e => { e.target.style.borderColor = '#C4B5D4'; e.target.style.boxShadow = 'none' }}
            />
          </VoiceButton>

          <p className="text-xs mt-2" style={{ color: '#C4B5D4' }}>
            {t('intention.staysPrivate')}
          </p>

          <div style={{ flex: 1 }} />

          <button
            onClick={handleContinue}
            className="w-full py-4 rounded-xl font-medium tracking-wide mt-6"
            style={{
              background: text.trim() ? '#3D1F62' : '#E4D5EE',
              color: text.trim() ? 'white' : '#9B8FAA',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {text.trim() ? t('session.continue') : t('intention.skipForNow')}
          </button>
        </div>

      </div>
    </div>
  )
}
